


























































































import { Component, Vue, Watch } from "vue-property-decorator";
import mapboxgl, {
  Map,
  MapboxEvent,
  Marker,
  LngLatBounds,
  GeoJSONSourceRaw,
  Layer
} from "mapbox-gl";
import { Location } from "@/util/types";
import { debounce } from "lodash";
import Geohash from "ngeohash";

const EVENT_BOUNDS_CHANGED = "bounds-changed";

const AppProps = Vue.extend({
  props: {
    height: Number,
    markerLocation: {
      type: Object as () => Location
    }
  }
});

@Component
export default class MyMap extends AppProps {
  style: string = `height:${this.height}px;`;

  map?: Map;
  marker?: Marker;
  bounds?: LngLatBounds;
  boundsLayer: string | null = null;
  mousePosition?: string;
  firstTime = 0;

  /**
   * Set class properties to null to make them reactive
   */
  data() {
    return {
      map: null,
      marker: null,
      bounds: null,
      mousePosition: null
    };
  }

  mounted() {
    mapboxgl.accessToken =
      "pk.eyJ1IjoiemVjaGRjIiwiYSI6ImNqcnB0eWIydTFoeDk0M256Mm5tZzA2ZTMifQ.s3pNPZGbaUGDOMlIfrTjnA";

    this.map = new mapboxgl.Map({
      container: "mymap",
      style: "mapbox://styles/mapbox/streets-v11",
      attributionControl: false // remove attribution
      // logoPosition: 'bottom-right',
    });

    this.map.addControl(
      new mapboxgl.GeolocateControl({
        fitBoundsOptions: {
          maxZoom: 9
        },
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true
      })
    );

    // register map events
    this.map.on("render", this.handleBoundsChange);

    this.map.on("mousemove", e => {
      this.mousePosition = `${e.lngLat}`;
    });

    // setup the inital map bounds
    this.handleBoundsChange();
  }

  handleBoundsChange() {
    if (!this.map) {
      return;
    }

    const mapBounds = this.map.getBounds();

    if (!this.bounds) {
      this.bounds = mapBounds;
      this.debouncedEmitBounds();
      return;
    }

    if (
      this.bounds.getWest() === mapBounds.getWest() &&
      this.bounds.getEast() === mapBounds.getEast() &&
      this.bounds.getSouth() === mapBounds.getSouth() &&
      this.bounds.getNorth() === mapBounds.getNorth()
    ) {
      // the bounds did not change, don't emit
      return;
    }

    this.bounds = mapBounds;
    this.debouncedEmitBounds();
  }

  get zoom(): number {
    if (!this.map) {
      return 0;
    }

    return this.map.getZoom();
  }

  get debouncedEmitBounds() {
    return debounce(() => {
      this.$emit(EVENT_BOUNDS_CHANGED, this.bounds, this.zoom);
    }, 100);
  }

  get boundsAsString() {
    if (!this.bounds) {
      return "";
    }

    return `n ${this.bounds
      .getNorth()
      .toFixed(2)}  s ${this.bounds
      .getSouth()
      .toFixed(2)}  w ${this.bounds
      .getWest()
      .toFixed(2)}  e ${this.bounds.getEast().toFixed(2)}`;
  }

  @Watch("markerLocation")
  onMarkerLocationChange(location: Location | null, oldVal: Location) {
    if (!this.map) {
      return;
    }

    // this.drawBoundingBox();
    // this.drawGeohash("e");
    // this.drawGeohash("et");
    // this.drawGeohash("etg");
    // this.drawGeohash("etgf");
    // this.drawGeohash("etgfq");
    // this.drawGeohash("etgfq7");
    // this.drawGeohash("etgfq7w");
    // this.drawGeohash("etgfq7w7");
    // this.drawGeohash("etgfq7w7p");

    if (this.marker) {
      this.marker.remove();
    }

    if (location) {
      this.marker = new mapboxgl.Marker({
        element: this.$refs.markerElement as HTMLElement,
        anchor: "bottom"
      })
        .setLngLat([location.lng, location.lat])
        .addTo(this.map);
    }
  }

  padBounds(bounds: LngLatBounds) {
    const north = this.addPadding(bounds.getNorth());
    const east = this.addPadding(bounds.getEast());
    bounds.setNorthEast([north, east]);

    const south = this.addPadding(bounds.getSouth());
    const west = this.addPadding(bounds.getWest());
    bounds.setSouthWest([south, west]);

    return bounds;
  }

  addPadding(cord: number) {
    let newCord = cord;
    let isNegative = false;
    if (newCord < 0) {
      isNegative = true;
      newCord = Math.abs(newCord);
    }

    newCord = newCord - 1;

    if (isNegative) {
      newCord = newCord * -1;
    }

    return newCord;
  }

  drawBoundingBox() {
    if (this.firstTime < 3) {
      this.firstTime++;
      return;
    }

    if (!this.bounds || !this.map) {
      return;
    }

    if (this.boundsLayer) {
      return;
      // this.map.removeLayer(this.boundsLayer);
    }

    const [min, max] = this.bounds.toArray();
    const east = this.bounds.getEast();
    const west = this.bounds.getWest();
    const south = this.bounds.getSouth();
    const north = this.bounds.getNorth();
    // console.log("east", east, "west", west, "south", south, "north", north);

    // geojson is lng, lat
    // https://macwright.org/lonlat/
    const coordinates = [
      [east, north],
      [west, north],
      [west, south],
      [east, south],
      [east, north]
    ];

    const source: GeoJSONSourceRaw = {
      type: "geojson",
      data: {
        type: "Feature",
        properties: {},
        geometry: {
          type: "Polygon",
          coordinates: [coordinates]
        }
      }
    };

    this.boundsLayer = `${Math.random()}`;
    const layer: Layer = {
      id: this.boundsLayer,
      type: "fill",
      source,
      layout: {},
      paint: {
        "fill-color": "#088",
        "fill-opacity": 0.3,
        "fill-outline-color": "#000"
      }
    };

    this.map.addLayer(layer);
  }

  // drawGeohash(geohash: string) {
  //   if (!this.map) {
  //     return;
  //   }
  //
  //   // [minlat, minlon, maxlat, maxlon]
  //   const [minlat, minlng, maxlat, maxlng] = Geohash.decode_bbox(geohash);
  //   console.log(minlat, minlng, maxlat, maxlng);
  //   // geojson is lng, lat
  //   // https://macwright.org/lonlat/
  //   const coordinates = [
  //     [minlng, minlat],
  //     [maxlng, minlat],
  //     [maxlng, maxlat],
  //     [minlng, maxlat],
  //     [minlng, minlat]
  //   ];
  //
  //   const source: GeoJSONSourceRaw = {
  //     type: "geojson",
  //     data: {
  //       type: "Feature",
  //       properties: {},
  //       geometry: {
  //         type: "Polygon",
  //         coordinates: [coordinates]
  //       }
  //     }
  //   };
  //
  //   const layer: Layer = {
  //     id: geohash,
  //     type: "fill",
  //     source,
  //     layout: {},
  //     paint: {
  //       "fill-color": "#088",
  //       "fill-opacity": 0.3,
  //       "fill-outline-color": "#000"
  //     }
  //   };
  //
  //   this.map.addLayer(layer);
  // }
}
